import React, { useState } from 'react';
import { 
  Building2, Baby, Car, Globe, HeartHandshake, 
  MapPin, ChevronRight, Search, Play, Zap, 
  Clock, ShieldCheck, FileCheck, BrainCircuit
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import HeaderActions from '../components/HeaderActions';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const OfficialProcedures = () => {
  const navigate = useNavigate();
  const { t } = useLanguage(); // Dil fonksiyonunu çağır
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Verileri dinamik olarak oluşturuyoruz (Dil değişince yenilenir)
  const procedures = [
    {
      id: 'anmeldung',
      title: t('proc_anmeldung_title') || 'Wohnsitzanmeldung',
      subtitle: t('proc_anmeldung_sub') || 'Residence Registration',
      category: 'relocation',
      icon: MapPin,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      automation: 90, 
      time: '15 min',
      desc: t('proc_anmeldung_desc') || 'Mandatory registration within 14 days of moving.',
      popular: true
    },
    {
      id: 'kindergeld',
      title: t('proc_kindergeld_title') || 'Kindergeld Antrag',
      subtitle: t('proc_kindergeld_sub') || 'Child Benefits',
      category: 'family',
      icon: Baby,
      color: 'text-rose-400',
      bg: 'bg-rose-500/10',
      automation: 85,
      time: '20 min',
      desc: t('proc_kindergeld_desc') || 'Get 250€ monthly per child.',
      popular: true
    },
    {
      id: 'bluecard',
      title: t('proc_bluecard_title') || 'Blue Card EU',
      subtitle: t('proc_bluecard_sub') || 'Work Visa Application',
      category: 'visa',
      icon: Globe,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      automation: 60,
      time: '45 min',
      desc: t('proc_bluecard_desc') || 'Step-by-step guidance for highly qualified professionals.',
      popular: false
    },
    {
      id: 'kfz',
      title: t('proc_kfz_title') || 'KFZ-Zulassung',
      subtitle: t('proc_kfz_sub') || 'Vehicle Registration',
      category: 'vehicle',
      icon: Car,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      automation: 75,
      time: '30 min',
      desc: t('proc_kfz_desc') || 'Register your car (eVB number required).',
      popular: false
    },
    {
      id: 'gewerbe',
      title: t('proc_gewerbe_title') || 'Gewerbeanmeldung',
      subtitle: t('proc_gewerbe_sub') || 'Trade Registration',
      category: 'business',
      icon: Building2,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      automation: 95,
      time: '10 min',
      desc: t('proc_gewerbe_desc') || 'Register your trade digitally with the local Gewerbeamt.',
      popular: true
    }
  ];

  // Kategori Filtreleme ve Arama Mantığı
  const filteredProcedures = procedures.filter(proc => {
      const matchesTab = activeTab === 'all' || proc.category === activeTab;
      const matchesSearch = proc.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            proc.subtitle.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesTab && matchesSearch;
  });

  // Tab Etiketleri (Dinamik)
  const getTabLabel = (tabKey) => {
      switch(tabKey) {
          case 'all': return t('tab_all');
          case 'relocation': return t('tab_relocation');
          case 'family': return t('tab_family');
          case 'visa': return t('tab_visa');
          case 'vehicle': return t('tab_vehicle');
          case 'business': return t('tab_business');
          default: return tabKey.toUpperCase();
      }
  };

  return (
    // Global Arka Plan (#0B1120)
    <div className="min-h-screen bg-transparent text-slate-100 font-sans flex flex-col relative overflow-hidden">
      
      {/* HEADER - Soft Glass */}
      <header className="sticky top-0 z-40 bg-[#0B1120]/80 backdrop-blur-xl border-b border-white/5 px-6 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
           <div className="flex items-center gap-4">
               <div className="p-2.5 bg-gradient-to-br from-indigo-600 to-indigo-500 rounded-xl text-white shadow-lg shadow-indigo-600/20">
                   <Building2 size={22} />
               </div>
               <div>
                   <h1 className="text-xl font-black uppercase tracking-tight text-white flex items-center gap-3 leading-none">
                      Fox<span className="text-indigo-500">Bureau</span>
                   </h1>
                   <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{t('fox_bureau_subtitle') || 'Official Procedures'}</p>
               </div>
           </div>
           
           <div className="hidden md:flex relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={16} />
                <Input 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('search_procedures') || "Search procedure..."} 
                    className="pl-10 h-10 bg-[#111827]/50 border-white/10 rounded-xl w-64 focus:border-indigo-500 transition-all font-medium text-xs shadow-sm text-white placeholder:text-slate-500 focus:bg-[#111827]"
                />
           </div>

           <HeaderActions />
        </div>
      </header>

      <main className="flex-1 w-full max-w-[1600px] mx-auto px-6 py-10 relative z-10 space-y-16">
        
        {/* HERO: THE FOX DIFFERENCE */}
        <section className="relative rounded-[3rem] overflow-hidden bg-gradient-to-r from-[#111827] to-[#0f172a] border border-white/10 shadow-2xl p-8 md:p-16 flex flex-col md:flex-row items-center gap-12 group">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none"></div>
            
            <div className="flex-1 space-y-6 relative z-10">
                <Badge className="bg-indigo-600 text-white border-0 font-bold uppercase tracking-widest text-[10px] px-3 py-1">
                    <BrainCircuit size={12} className="mr-2"/> Fox AI Engine
                </Badge>
                <h2 className="text-4xl md:text-6xl font-black text-white uppercase italic tracking-tighter leading-[0.9]">
                    {t('ai_is_fast') || "Don't speak"} <br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">"Behördendeutsch"?</span>
                </h2>
                <p className="text-slate-400 font-medium text-lg leading-relaxed max-w-xl">
                    {t('mod_bureaucracy_desc') || "Traditional forms are complex, German-only, and error-prone. BüroFuchs translates, simplifies, and auto-fills them for you."}
                </p>
                
                <div className="flex gap-4 pt-4">
                    <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Traditional</span>
                        <div className="h-2 w-32 bg-red-500/20 rounded-full overflow-hidden">
                            <div className="h-full w-[20%] bg-red-500"></div>
                        </div>
                        <span className="text-xs font-bold text-red-400">Slow</span>
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Fox AI</span>
                        <div className="h-2 w-48 bg-emerald-500/20 rounded-full overflow-hidden">
                            <div className="h-full w-[95%] bg-emerald-500"></div>
                        </div>
                        <span className="text-xs font-bold text-emerald-400">Fast</span>
                    </div>
                </div>
            </div>

            {/* Visual Element: Floating Glass Cards */}
            {/* ... (Visual kısımlar aynı kalabilir, metin içermiyor) ... */}
        </section>

        {/* PROCEDURES GRID */}
        <div>
            <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-black text-white uppercase tracking-widest flex items-center gap-2">
                    <HeartHandshake className="text-indigo-500"/> {t('official_procedures') || 'Available Procedures'}
                </h3>
                <div className="flex bg-[#111827] rounded-xl p-1 border border-white/10 overflow-x-auto no-scrollbar">
                    {['all', 'relocation', 'family', 'visa', 'vehicle', 'business'].map(tab => (
                        <button 
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all whitespace-nowrap ${
                                activeTab === tab 
                                ? 'bg-white text-[#0B1120] shadow-sm' 
                                : 'text-slate-500 hover:text-white'
                            }`}
                        >
                            {getTabLabel(tab)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProcedures.map((proc, idx) => (
                    <motion.div 
                        key={proc.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        whileHover={{ y: -5 }}
                        className="group relative bg-[#111827] rounded-[2rem] border border-white/5 hover:border-indigo-500/30 transition-all shadow-xl overflow-hidden cursor-pointer"
                        onClick={() => navigate(`/procedure/${proc.id}`)}
                    >
                        {/* Popular Badge */}
                        {proc.popular && (
                            <div className="absolute top-0 right-0 bg-indigo-600 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-bl-xl z-20">
                                {t('popular_badge') || 'Popular'}
                            </div>
                        )}

                        <div className="p-8 relative z-10 h-full flex flex-col">
                            <div className="flex justify-between items-start mb-6">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${proc.bg} ${proc.color} shadow-lg`}>
                                    <proc.icon size={28} />
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-black text-white">{proc.automation}%</div>
                                    <div className="text-[9px] text-slate-500 font-bold uppercase tracking-wide">{t('automated') || 'Automated'}</div>
                                </div>
                            </div>

                            <h3 className="text-xl font-black text-white mb-1">{proc.title}</h3>
                            <p className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-4">{proc.subtitle}</p>
                            
                            <p className="text-sm text-slate-400 leading-relaxed mb-8 flex-1">
                                {proc.desc}
                            </p>

                            <div className="pt-6 border-t border-white/5 flex items-center justify-between">
                                <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                                    <Clock size={14}/> {proc.time}
                                </div>
                                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-white group-hover:text-indigo-400 transition-colors">
                                    {t('start_process') || 'Start Process'} <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform"/>
                                </div>
                            </div>
                        </div>

                        {/* Hover Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                    </motion.div>
                ))}
            </div>
        </div>

      </main>

      <div className="relative z-10 mt-10">
         <Footer />
      </div>
    </div>
  );
};

export default OfficialProcedures;